import { AxiosResponse } from "axios";
import { useEffect, useState } from "react";

import axiosInstance from "../api/axiosConfig";

interface IApiResponse<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  execute: (_newParams?: any) => Promise<T | undefined>; // ✅ Cho phép truyền params động
  options?: IFetchOptions;
}
interface IFetchOptions {
  instance?: any;
  autoFetch?: boolean;
}

export type FetchMethod = "POST" | "PUT" | "DELETE" | "PATCH" | "GET";

/**
 * Fetch data from API
 * @param url - your API endpoint. This MUST be defined in src/utils/constants/Apis.ts.
 *              Ensure the URL is correctly set up in the constants file before using this hook.
 * @param method HTTP method (GET, POST, PUT, DELETE)
 * @param params request body (for POST, PUT, DELETE)
 * @param query request query (for GET)
 * @param options flag to check if this data need to be automatically fetched or manually fetched
 * @returns {IApiResponse} - data, loading, error, execute
 * @example
 * const { data, loading, error, execute } = useFetch<Data | Data[]>(API.YOUR_API_ENDPOINT, 'GET');
 */
const useMutation = <T>(
  url: string,
  method: FetchMethod = "POST",
  params?: any,
  query?: any,
  options?: IFetchOptions
): IApiResponse<T> => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [loadFirstTime, setLoadFirstTime] = useState<boolean>(false);
  const { instance = axiosInstance, autoFetch = false } = options ?? {};
  const execute = async (newParams?: any) => {
    setLoading(true);
    setError(null);

    try {
      let response: AxiosResponse<T>;
      if (!url) {
        return;
      }
      switch (method.toUpperCase()) {
        case "POST":
          response = await instance.post(url, newParams ?? params);
          break;
        case "GET":
          response = await instance.get(url, newParams ?? params);
          break;
        case "PUT":
          response = await instance.put(url, newParams ?? params);
          break;
        case "DELETE":
          response = await instance.delete(url, {
            data: newParams ?? params,
            params: query,
          });
          break;
        case "PATCH":
          response = await instance.patch(url, newParams ?? params, {
            params: query,
          });
          break;

        // Add cases for other HTTP methods if needed
        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }
      setData(response.data);
      return response.data;
    } catch (error) {
      setError(error as Error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loadFirstTime && !autoFetch) {
      return;
    }
    if (url) {
      execute();
      setLoadFirstTime(true);
    }

    // Cleanup function
    return () => {
      // Cleanup logic if needed
    };
  }, [url, method, params, query, autoFetch]);

  useEffect(() => {
    setLoadFirstTime(false);
  }, []);

  return { data, loading, error, execute };
};

export default useMutation;
